import * as Sentry from '@sentry/react';
import React, { useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

// Pages
import AuthRoutes from './routes/Auth';
import NoAuthRoutes from './routes/NoAuth';

// Utilities
import { AdapterDayjs } from '@adamftd/x-date-pickers-pro/AdapterDayjs';
import { LocalizationProvider } from '@adamftd/x-date-pickers-pro/LocalizationProvider';
import { useScrollToTop } from 'src/hooks/useScrollToTop';
import localization from 'src/localization';
import { IP_ADDRESS_KEY, PLAN_INFO_FROM_HERCULES_PAGE, REDIRECT, REDIRECT_CONFIG } from 'src/utils/constants/config';
import { FormType, useFormSearch } from 'src/zustand/useFormSearch';

// Components
import analytics from 'src/analytics';
import WarningSensitiveImage from 'src/components/common/WarningSensitiveImage';
import FormSearchCompany from 'src/features/CompanyDetail/Common/FormSearchCompany';
import FormCompareCompany from 'src/features/CompareCompany/Common/FormCompareCompany';
import FormSearchPeople from 'src/features/PeopleDetail/Common/FormSearchPeople';
import FormSearchTradeData from 'src/features/TradeData/Common/FormSearchTradeData';
import NotFoundPage from 'src/pages/NotFound';
// import NotifyCreditCosts from 'src/components/Popup/NotifyCreditCosts';
import { HttpStatusCode } from 'axios';
import { has } from 'lodash';
import FeatureRestriction from 'src/components/Popup/FeatureRestriction';
import NotifyNotEnoughCredit from 'src/components/Popup/NotifyNotEnoughCredit';
import FormSearchAnalyticsPackage from 'src/features/AnalyticsPackage/Common/FormSearchAnalyticsPackage';
import FormSearchPort from 'src/features/ListPorts/Common/FormSearchPort';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useQueryUsageInformation, useQueryUserInfo } from 'src/services/queries';
import { useQueryRuntimeConfig } from 'src/services/queries/useQueryRuntimeConfig';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { handleQueryParamFromObject } from 'src/utils/helpers/queryParam';
import { NotifyType, useNotifyCreditCosts } from 'src/zustand/useNotifyCreditCosts';
import { isJsonString } from './utils/helpers/stringHelpers';

localization.init();
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

function App() {
  useScrollToTop();
  useQueryRuntimeConfig(); // Pre-fetch config to get cached config for other pages
  analytics.initialize();
  const { handleOpenNotify } = useNotifyCreditCosts((state) => state);
  const location = useLocation();
  const navigate = useNavigate();
  const { data } = useQueryUserInfo();
  const { data: dataUsage, isFetching: isFetchingUsage } = useQueryUsageInformation();
  const { type, visible, selectedCompanyCompare, handleCloseSearch } = useFormSearch((state) => state);
  const hasHistoryEntryExist = location.key !== 'default'; // false => chuyển từ 1 trang ngoài Adamftd qua
  const { selected_plan = '', selected_plan_type = 'monthly' } = useQueryParams();
  const whitedListRoutesWhenNoPlanRegistered = [
    AuthRouteKeys.Onboarding,
    AuthRouteKeys.Checkout,
    AuthRouteKeys.CalendarBooking,
    AuthRouteKeys.BankTransfer,
  ];

  useEffect(() => {
    console.log('Adamftd App Version:', process.env.REACT_APP_VERSION);
    console.log('Debug: ', process.env.REACT_APP_VERSION);
  }, []);

  // Get Client's IP Address
  function getIPFromAmazon() {
    fetch('https://checkip.amazonaws.com/')
      .then((res) => res.text())
      .then((data) => {
        localStorage.setItem(IP_ADDRESS_KEY, data);
        return data;
      })
      .catch((error) => {
        console.log('🚀 ~ :78 ~ getIPFromAmazon ~ error:', error);
        return;
      });
  }

  useEffect(() => {
    if (selected_plan) {
      localStorage.setItem(
        PLAN_INFO_FROM_HERCULES_PAGE,
        JSON.stringify({ id: selected_plan, type: selected_plan_type }),
      );
      sessionStorage.setItem(REDIRECT, REDIRECT_CONFIG.PAYMENT);
    }
  }, []);

  useEffect(() => {
    (window as any).testThrowError = (errMsg: string) => {
      Sentry.captureException(new Error(errMsg));
    };
  }, []);

  useEffect(() => {
    getIPFromAmazon();
  }, []);

  useEffect(() => {
    const isNotFoundPlanUsage = dataUsage?.status_code === HttpStatusCode.NotFound;
    const listRouter = Object.values(AuthRouteKeys).filter(
      (route) => !whitedListRoutesWhenNoPlanRegistered.includes(route),
    );
    const redirectConfig = sessionStorage.getItem(REDIRECT);
    const selectedPlanInfo = localStorage.getItem(PLAN_INFO_FROM_HERCULES_PAGE);
    const selectedPlanInfoObj =
      selectedPlanInfo && isJsonString(selectedPlanInfo || '') ? JSON.parse(selectedPlanInfo) : {};
    const userType = data?.data.type;
    const shouldCheckRoute = listRouter.includes(location.pathname as AuthRouteKeys);
    const isShouldRedirectToPricingPage = shouldCheckRoute && !isFetchingUsage && isNotFoundPlanUsage;

    const isShouldRedirectToCheckoutPage =
      isShouldRedirectToPricingPage && redirectConfig === REDIRECT_CONFIG.PAYMENT && has(selectedPlanInfoObj, 'id');
    /** --- Temporary remove Onboarding step --- */
    // const isRedirectToOnboarding = userType === UserType.Guest && shouldCheckRoute;
    const isRedirectToOnboarding = false;

    const isEmptyCredits = dataUsage?.data.id && dataUsage?.data?.usage_total - dataUsage?.data?.current_usage === 0;
    if (isShouldRedirectToCheckoutPage) {
      const params = handleQueryParamFromObject({
        planId: selectedPlanInfoObj?.id,
        type: selectedPlanInfoObj?.type,
      });
      navigate({ pathname: AuthRouteKeys.Checkout, search: params });
    } else if (isShouldRedirectToPricingPage) navigate(AuthRouteKeys.Pricing);

    const billableRoutes = [
      AuthRouteKeys.CompanyProfiles,
      AuthRouteKeys.CompanyDetail,
      AuthRouteKeys.PeopleProfiles,
      AuthRouteKeys.PeopleDetail,
      AuthRouteKeys.TradeData,
      AuthRouteKeys.CompareCompany,
    ];

    const rootPathName = location.pathname.split('/').filter(Boolean)[0]; // Get the root path

    if (isEmptyCredits && billableRoutes.find((route) => route.includes(rootPathName)) && location.pathname !== '/') {
      handleOpenNotify({
        type: NotifyType.NotEnoughCredit,
        showCloseIcon: true,
        cbClose: () => {
          navigate(AuthRouteKeys.Home);
        },
      });
    }

    if (isRedirectToOnboarding) navigate(AuthRouteKeys.Onboarding);
  }, [location, JSON.stringify(dataUsage), data]);

  return (
    <React.Fragment>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <FormSearchCompany open={type === FormType.SearchCompany && visible} handleClose={handleCloseSearch} />
        <FormSearchPeople open={type === FormType.SearchPeople && visible} handleClose={handleCloseSearch} />
        <FormSearchTradeData open={type === FormType.SearchTradeData && visible} handleClose={handleCloseSearch} />
        <FormSearchPort open={type === FormType.SearchPort && visible} handleClose={handleCloseSearch} />
        <FormSearchAnalyticsPackage
          open={type === FormType.AnalyticsPackage && visible}
          handleClose={handleCloseSearch}
        />

        {/*<NotifyCreditCosts />*/}
        <NotifyNotEnoughCredit />
        <FeatureRestriction />
        <FormCompareCompany
          open={type === FormType.CompareCompany && visible}
          handleClose={handleCloseSearch}
          selectedCompanyCompare={selectedCompanyCompare}
        />
        <WarningSensitiveImage
          open={type === FormType.WarningSensitiveImage && visible}
          handleClose={handleCloseSearch}
        />
        <SentryRoutes>
          {NoAuthRoutes.map((route) => route)}
          {AuthRoutes.map((route) => route)}
          <Route path="/*" element={<NotFoundPage />} />
        </SentryRoutes>
      </LocalizationProvider>
    </React.Fragment>
  );
}

export default App;
